import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Sparkles, X, RefreshCw, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { aiChapterBeatsService } from '@/services/ai-service';
import { useLicense } from '@/hooks/useLicense';
import { useState } from 'react';
import { cn } from '@/lib/utils';

export interface SceneCard {
  id: string;
  title: string;
  description: string;
}

interface PlanScenesStepProps {
  scenes: SceneCard[];
  onScenesChange: (scenes: SceneCard[]) => void;
  chapterName: string;
  chapterDescription?: string;
  chapterId?: number;
  storyId?: string;
  projectId?: string;
}

export function PlanScenesStep({
  scenes,
  onScenesChange,
  chapterName,
  chapterDescription,
  chapterId,
  storyId,
  projectId,
}: PlanScenesStepProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const { hasAIFeatures } = useLicense();
  const handleAddScene = () => {
    const newScene: SceneCard = {
      id: crypto.randomUUID(),
      title: `Scene ${scenes.length + 1}`,
      description: '',
    };
    onScenesChange([...scenes, newScene]);
  };

  const handleDeleteScene = (id: string) => {
    onScenesChange(scenes.filter(s => s.id !== id));
  };

  const handleUpdateScene = (id: string, field: 'title' | 'description', value: string) => {
    onScenesChange(
      scenes.map(s => s.id === id ? { ...s, [field]: value } : s)
    );
  };

  const handleGenerateNextScene = async (userPrompt?: string) => {
    if (isGenerating) return;
    setIsGenerating(true);

    try {
      const response = await aiChapterBeatsService.streamGenerateBeat(
        chapterId,
        scenes.map(s => ({ name: s.title, description: s.description })),
        userPrompt,
        undefined,
        chapterName,
        chapterDescription,
        projectId,
        storyId
      );

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        
        // Very basic parsing for streaming object - typically Vercel AI SDK sends partial JSON
        // In a real app we'd use a robust partial JSON parser or a specific hook
        try {
          // Attempt to find the object in the stream
          // For simplicity, we'll just wait for the full object if we can't parse partial
          // But actually, the backend sends a single JSON object in this case if not using streamObject
          // Let's check if it's just one object at the end
        } catch (e) {}
      }

      // Final parse
      try {
        const result = JSON.parse(buffer);
        if (result && result.name) {
          const newScene: SceneCard = {
            id: crypto.randomUUID(),
            title: result.name,
            description: result.description || '',
          };
          onScenesChange([...scenes, newScene]);
        }
      } catch (e) {
        console.error('Failed to parse generated beat:', buffer);
      }
    } catch (error) {
      console.error('Error generating scene:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateScene = async (id: string) => {
    if (regeneratingId) return;
    const sceneToRegenerate = scenes.find(s => s.id === id);
    if (!sceneToRegenerate) return;

    setRegeneratingId(id);

    try {
      const response = await aiChapterBeatsService.streamGenerateBeat(
        chapterId,
        scenes.filter(s => s.id !== id).map(s => ({ name: s.title, description: s.description })),
        undefined,
        { name: sceneToRegenerate.title, description: sceneToRegenerate.description },
        chapterName,
        chapterDescription,
        projectId,
        storyId
      );

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');

      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
      }

      const result = JSON.parse(buffer);
      if (result && result.name) {
        onScenesChange(
          scenes.map(s => s.id === id ? { ...s, title: result.name, description: result.description || '' } : s)
        );
      }
    } catch (error) {
      console.error('Error regenerating scene:', error);
    } finally {
      setRegeneratingId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex flex-col gap-8 w-full max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="space-y-2 text-center border-b border-border/40 pb-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight text-foreground font-lora leading-tight">
            {chapterName}
          </h1>
        </div>
        <p className="text-[15px] text-muted-foreground max-w-xl mx-auto leading-relaxed">
          Break this chapter into specific scenes. Use the fields below to define key events and character beats for each moment.
        </p>
      </div>

      {/* Scene Cards */}
      <div className="space-y-4">
        <AnimatePresence mode="popLayout">
          {scenes.map((scene, index) => (
            <motion.div
              key={scene.id}
              layout
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <Card className="rounded-2xl border-border bg-card/50 shadow-sm overflow-hidden group hover:border-primary/20 hover:shadow-md transition-all duration-300">
                {/* Card Header */}
                <div className="flex items-center justify-between px-5 py-3 bg-muted/20 border-b border-border/40">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shadow-inner">
                      {index + 1}
                    </div>
                    <span className="text-[11px] font-bold text-muted-foreground/80 uppercase tracking-widest">Scene {index + 1}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {hasAIFeatures && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRegenerateScene(scene.id)}
                      disabled={!!regeneratingId}
                      className="h-7 w-7 text-muted-foreground/30 hover:text-primary hover:bg-primary/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                      title="Regenerate scene"
                    >
                      <RefreshCw className={cn("h-3.5 w-3.5", regeneratingId === scene.id && "animate-spin")} />
                    </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteScene(scene.id)}
                      className="h-7 w-7 text-muted-foreground/30 hover:text-destructive hover:bg-destructive/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5 space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 px-0.5">Title</label>
                    <Input
                      value={scene.title}
                      onChange={(e) => handleUpdateScene(scene.id, 'title', e.target.value)}
                      placeholder="Give this scene a name..."
                      className="h-10 rounded-xl border-border bg-background/50 text-[15px] font-medium focus-visible:ring-primary/20 transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 px-0.5">Description & Context</label>
                    <Textarea
                      value={scene.description}
                      onChange={(e) => handleUpdateScene(scene.id, 'description', e.target.value)}
                      placeholder="Describe what happens in this scene — key events, character development, or setting changes..."
                      className="min-h-[100px] rounded-xl border-border bg-background/50 text-sm leading-relaxed resize-none focus-visible:ring-primary/20 transition-all shadow-none"
                    />
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Add Scene Button */}
        <Button
          onClick={handleAddScene}
          variant="ghost"
          className="w-full h-10 rounded-xl border border-dashed border-border/60 hover:border-primary/40 hover:bg-primary/5 text-muted-foreground hover:text-primary transition-all gap-2 text-xs font-medium"
        >
          <Plus className="h-3.5 w-3.5" />
          Add a scene
        </Button>
      </div>

      {/* AI Generate Button - only for AI-enabled plans */}
      {hasAIFeatures && (
      <Card className="rounded-xl border-border bg-muted/20 backdrop-blur-sm p-4">
        <Button
          variant="ghost"
          onClick={() => handleGenerateNextScene()}
          disabled={isGenerating}
          className="w-full h-10 rounded-xl bg-background hover:bg-background/80 text-foreground font-medium text-xs gap-2 border border-border/60"
        >
          {isGenerating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
          ) : (
            <Sparkles className="h-3.5 w-3.5 text-primary" />
          )}
          {isGenerating ? 'Generating...' : 'Generate next scene with AI'}
        </Button>
      </Card>
      )}
    </motion.div>
  );
}
